import React, { useState } from "react";
import {
  Card,
  Text,
  Group,
  Stack,
  Button,
  ActionIcon,
  TextInput,
  Table,
  NumberInput,
  Modal,
  Box,
} from "@mantine/core";
import { IconEdit, IconX, IconPlus } from "@tabler/icons-react";

/**
 * ScalesPreview Component
 *
 * A reusable component that displays pay scales in a card format with optional editing capabilities.
 * Shows revenue thresholds with corresponding commission percentages.
 *
 * @param {Object} props
 * @param {Array} props.scales - Array of scale objects with revenue and percentage
 * @param {string} props.name - Name/title for the scale
 * @param {string} props.createdAt - Creation date (optional)
 * @param {Function} props.onEdit - Optional callback for edit action (receives updated data)
 * @param {Function} props.onDelete - Optional callback for delete action
 * @param {boolean} props.showActions - Whether to show edit/delete actions (default: true)
 * @param {boolean} props.showFooter - Whether to show footer with date (default: true)
 * @param {boolean} props.isEmpty - Whether this is an empty skeleton template (default: false)
 */
function ScalesPreview({
  scale_lines = [],
  name = "",
  createdAt = "",
  onEdit,
  onDelete,
  showActions = true,
  showFooter = true,
  customActionElements,
  isEmpty = false,
  compactRows = false,
  fixedRowsVisible = null,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingName, setEditingName] = useState(name);
  const [editingScales, setEditingScales] = useState([...scale_lines]);

  // Default currency formatter if none provided
  const defaultFormatCurrency = value => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const currencyFormatter = defaultFormatCurrency;

  // Don't render if no scale_lines provided
  if (!scale_lines || scale_lines.length === 0) {
    return null;
  }

  const handleEdit = () => {
    setIsEditing(true);
    setEditingName(name);
    setEditingScales([...scale_lines]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingName(name);
    setEditingScales([...scale_lines]);
  };

  const handleDelete = () => {
    setIsDeleting(true);
  };

  const handleCancelDelete = () => {
    setIsDeleting(false);
  };

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setIsDeleting(false);
  };

  const handleSave = () => {
    if (onEdit) {
      // Filter out empty rows
      const validScales = editingScales.filter(
        scale => scale.revenue_amount || scale.percentage
      );

      onEdit({
        name: editingName,
        scale_lines: validScales,
      });
    }
    setIsEditing(false);
  };

  const handleScaleChange = (id, field, value) => {
    setEditingScales(prev =>
      prev.map(item => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const addRow = () => {
    const newId = Math.max(...editingScales.map(item => item.id || 0), 0) + 1;
    setEditingScales(prev => [
      ...prev,
      {
        id: newId,
        revenue_amount: "",
        percentage: "",
      },
    ]);
  };

  const removeRow = id => {
    if (editingScales.length > 1) {
      setEditingScales(prev => prev.filter(item => item.id !== id));
    }
  };

  // Render edit mode
  const renderEditMode = () => (
    <Stack gap="md">
      <TextInput
        label="Template Name"
        value={editingName}
        onChange={e => setEditingName(e.target.value)}
        placeholder="e.g., Standard Commission, Premium Rate, etc."
        required
      />

      <Stack gap="xs">
        <Text fw={500}>Scale Configuration</Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Percentage (%)</Table.Th>
              <Table.Th>Revenue ($)</Table.Th>
              <Table.Th width={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {editingScales.map(item => (
              <Table.Tr key={item.id}>
                <Table.Td>
                  <NumberInput
                    value={item.percentage}
                    onChange={value =>
                      handleScaleChange(item.id, "percentage", value || "")
                    }
                    placeholder="0.00"
                    min={0}
                    max={100}
                    decimalScale={2}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={item.revenue_amount}
                    onChange={value =>
                      handleScaleChange(item.id, "revenue_amount", value || "")
                    }
                    placeholder="0.00"
                    min={0}
                    decimalScale={2}
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => removeRow(item.id)}
                    disabled={editingScales.length <= 1}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Group justify="flex-start">
          <Button
            variant="outline"
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={addRow}
          >
            Add Row
          </Button>
        </Group>
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save Changes</Button>
      </Group>
    </Stack>
  );

  // Render preview mode
  const renderPreviewMode = () => {
    const rowHeight = compactRows ? 24 : 40;
    const fixedRowsContainerStyle =
      typeof fixedRowsVisible === "number" && fixedRowsVisible > 0
        ? {
            minHeight: `${rowHeight * fixedRowsVisible}px`,
            maxHeight: `${rowHeight * fixedRowsVisible}px`,
            overflowY: "auto",
            paddingRight: 4,
          }
        : undefined;

    if (isEmpty) {
      return (
        <Stack gap="xs">
          <Group
            justify="space-between"
            p="sm"
            bg="gray.1"
            style={{
              borderRadius: "4px",
              border: "2px dashed #dee2e6",
              opacity: 0.6,
            }}
          >
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              $0
            </Text>
            <Text c="dimmed">→</Text>
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              0%
            </Text>
          </Group>
          <Text
            size="sm"
            c="dimmed"
            ta="center"
            mt="xs"
            style={{ fontStyle: "italic" }}
          >
            Click to configure
          </Text>
        </Stack>
      );
    }

    return (
      <Box style={fixedRowsContainerStyle}>
        <Stack gap={compactRows ? 4 : "xs"}>
          {scale_lines.map((scale, index) => (
            <Group
              key={scale.id || index}
              justify="space-between"
              p={compactRows ? "xs" : "sm"}
              bg="gray.0"
              style={{ borderRadius: "4px", minHeight: `${rowHeight}px` }}
            >
              <Text fw={500} size={compactRows ? "xs" : "sm"}>
                {currencyFormatter(scale.revenue_amount)}
              </Text>
              <Text c="dimmed" size={compactRows ? "xs" : "sm"}>
                →
              </Text>
              <Text fw={500} size={compactRows ? "xs" : "sm"}>
                {scale.percentage}%
              </Text>
            </Group>
          ))}
        </Stack>
      </Box>
    );
  };

  // Render full card layout
  return (
    <>
      {/* Delete Confirmation Modal */}
      <Modal
        opened={isDeleting}
        onClose={handleCancelDelete}
        title="Delete Template"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text>Are you sure you want to delete this template?</Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Card.Section withBorder inheritPadding py="sm">
          <Group justify="space-between" align="center">
            <Text fw={600}>{isEditing ? "Editing Template" : name}</Text>
            {customActionElements}
            {showActions && !isEditing && (
              <Group gap="xs">
                {onEdit && (
                  <ActionIcon
                    variant="subtle"
                    onClick={handleEdit}
                    title="Edit Template"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                )}
                {onDelete && (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={handleDelete}
                    title="Delete Template"
                  >
                    <IconX size={20} />
                  </ActionIcon>
                )}
              </Group>
            )}
          </Group>
        </Card.Section>

        <Box p="md">{isEditing ? renderEditMode() : renderPreviewMode()}</Box>

        {showFooter && !isEditing && createdAt && (
          <Card.Section withBorder inheritPadding py="xs" bg="gray.0">
            <Text size="xs" c="dimmed">
              Created: {new Date(createdAt).toLocaleDateString()}
            </Text>
          </Card.Section>
        )}
      </Card>
    </>
  );
}

export default ScalesPreview;
